import React, { useState, useEffect } from 'react';
import { Button, Modal, Form, Input, message, Space, Alert, Spin, Tag } from 'antd';
import { PlayCircleOutlined, CheckCircleOutlined, CloseCircleOutlined } from '@ant-design/icons';
import { StateMachineService } from '../../services/api/stateMachineService';
import {
  AvailableTransition,
  TransitionValidationResult,
  TransitionExecuteRequest,
} from '../../types/stateMachine';
import { ArtifactItem } from '../../types/artifactItems';

const { TextArea } = Input;

interface TransitionExecutorProps {
  artifactId: string;
  item: ArtifactItem;
  onTransitionComplete?: () => void;
  visible?: boolean;
  onCancel?: () => void;
}

export const TransitionExecutor: React.FC<TransitionExecutorProps> = ({
  artifactId,
  item,
  onTransitionComplete,
  visible = false,
  onCancel,
}) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [availableTransitions, setAvailableTransitions] = useState<AvailableTransition[]>([]);
  const [validations, setValidations] = useState<Map<string, TransitionValidationResult>>(
    new Map()
  );
  const [selectedTransition, setSelectedTransition] = useState<AvailableTransition | null>(null);
  const [executing, setExecuting] = useState(false);

  useEffect(() => {
    if (visible && item.id) {
      void loadAvailableTransitions();
    }
  }, [visible, item.id, artifactId]);

  const loadAvailableTransitions = async () => {
    try {
      setLoading(true);
      const transitions = await StateMachineService.getAvailableTransitions(artifactId, item.id);
      setAvailableTransitions(transitions);

      // Pre-validate all transitions
      const validationPromises = transitions.map(async (transition) => {
        try {
          const validation = await StateMachineService.validateTransition(
            artifactId,
            item.id,
            transition.id
          );
          return { transitionId: transition.id, validation };
        } catch (error) {
          console.error(`Failed to validate transition ${transition.id}:`, error);
          return {
            transitionId: transition.id,
            validation: {
              can_execute: false,
              errors: ['Failed to validate transition'],
              warnings: [],
              conditions_met: false,
            } as TransitionValidationResult,
          };
        }
      });

      const validationResults = await Promise.all(validationPromises);
      const validationMap = new Map<string, TransitionValidationResult>();
      validationResults.forEach(({ transitionId, validation }) => {
        validationMap.set(transitionId, validation);
      });
      setValidations(validationMap);
    } catch (error: unknown) {
      console.error('Error loading available transitions:', error);
      message.error('Failed to load available transitions');
    } finally {
      setLoading(false);
    }
  };

  const handleTransitionSelect = (transition: AvailableTransition) => {
    setSelectedTransition(transition);
    form.resetFields();
  };

  const handleExecute = async () => {
    if (!selectedTransition) return;

    try {
      const values = await form.validateFields();
      setExecuting(true);

      const executeData: TransitionExecuteRequest = {
        transition_data: values.transition_data || {},
        reason: values.reason || '',
        transitioned_by: values.transitioned_by,
      };

      await StateMachineService.executeTransition(
        artifactId,
        item.id,
        selectedTransition.id,
        executeData
      );

      message.success(`Transition "${selectedTransition.transition_name ?? selectedTransition.name}" executed successfully`);
      form.resetFields();
      setSelectedTransition(null);
      onTransitionComplete?.();
    } catch (error: unknown) {
      console.error('Error executing transition:', error);
      const err = error as { response?: { data?: { detail?: string } }; message?: string };
      message.error(
        err.response?.data?.detail ?? err.message ?? 'Failed to execute transition'
      );
    } finally {
      setExecuting(false);
    }
  };

  const getValidationStatus = (transitionId: string) => {
    const validation = validations.get(transitionId);
    if (!validation) return null;

    if (validation.can_execute) {
      return <Tag color="green" icon={<CheckCircleOutlined />}>Ready</Tag>;
    } else {
      return <Tag color="red" icon={<CloseCircleOutlined />}>Cannot Execute</Tag>;
    }
  };

  if (!visible) return null;

  return (
    <Modal
      title="Execute Transition"
      open={visible}
      onCancel={onCancel}
      footer={null}
      width={800}
      destroyOnHidden
    >
      <div style={{ marginBottom: 16 }}>
        <strong>Item:</strong> {item.name || item.id}
        <br />
        <strong>Current State:</strong> {item.current_state_id || item.state_id || item.state}
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', padding: 24 }}>
          <Spin size="large" />
          <div style={{ marginTop: 16 }}>Loading available transitions...</div>
        </div>
      ) : availableTransitions.length === 0 ? (
        <Alert
          message="No Available Transitions"
          description="There are no transitions available for this item in its current state."
          type="info"
        />
      ) : (
        <div>
          <div style={{ marginBottom: 16 }}>
            <h4>Available Transitions</h4>
            <Space direction="vertical" style={{ width: '100%' }} size="small">
              {availableTransitions.map((transition) => {
                const validation = validations.get(transition.id);
                const isSelected = selectedTransition?.id === transition.id;
                const canExecute = validation?.can_execute ?? false;

                return (
                  <div
                    key={transition.id}
                    style={{
                      padding: 12,
                      border: isSelected ? '2px solid #1890ff' : '1px solid #d9d9d9',
                      borderRadius: 4,
                      cursor: 'pointer',
                      backgroundColor: isSelected ? '#e6f7ff' : '#fff',
                    }}
                    onClick={() => handleTransitionSelect(transition)}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <strong>{transition.transition_name ?? transition.name}</strong>
                          {(transition.is_automatic ?? transition.isAutomatic) && (
                            <Tag color="orange">Automatic</Tag>
                          )}
                        </div>
                        {transition.description && (
                          <div style={{ color: '#666', fontSize: 12, marginTop: 4 }}>
                            {transition.description}
                          </div>
                        )}
                        <div style={{ fontSize: 12, color: '#999', marginTop: 4 }}>
                          {transition.from_state_name ?? 'Unknown'} →{' '}
                          {transition.to_state_name ?? 'Unknown'}
                        </div>
                      </div>
                      <div>{getValidationStatus(transition.id)}</div>
                    </div>

                    {validation && !validation.can_execute && validation.errors.length > 0 && (
                      <Alert
                        message="Validation Errors"
                        description={
                          <ul style={{ margin: 0, paddingLeft: 20 }}>
                            {validation.errors.map((error, idx) => (
                              <li key={idx}>{error}</li>
                            ))}
                          </ul>
                        }
                        type="error"
                        style={{ marginTop: 8 }}
                        showIcon
                      />
                    )}

                    {validation && validation.warnings.length > 0 && (
                      <Alert
                        message="Warnings"
                        description={
                          <ul style={{ margin: 0, paddingLeft: 20 }}>
                            {validation.warnings.map((warning, idx) => (
                              <li key={idx}>{warning}</li>
                            ))}
                          </ul>
                        }
                        type="warning"
                        style={{ marginTop: 8 }}
                        showIcon
                      />
                    )}
                  </div>
                );
              })}
            </Space>
          </div>

          {selectedTransition && (
            <div>
              <h4>Execute Transition</h4>
              <Form form={form} layout="vertical">
                <Form.Item
                  label="Transition Data (JSON)"
                  name="transition_data"
                  tooltip="Optional data to pass with the transition"
                >
                  <TextArea
                    rows={4}
                    placeholder='{"quantity": 1, "notes": "Starting production"}'
                  />
                </Form.Item>

                <Form.Item
                  label="Reason"
                  name="reason"
                  rules={[{ required: true, message: 'Please provide a reason for this transition' }]}
                >
                  <TextArea rows={2} placeholder="Why are you executing this transition?" />
                </Form.Item>

                <Form.Item label="Transitioned By (User ID)" name="transitioned_by">
                  <Input placeholder="Optional: User ID who is executing this transition" />
                </Form.Item>

                <Form.Item>
                  <Space>
                    <Button
                      type="primary"
                      icon={<PlayCircleOutlined />}
                      onClick={handleExecute}
                      loading={executing}
                      disabled={!validations.get(selectedTransition.id)?.can_execute}
                    >
                      Execute Transition
                    </Button>
                    <Button onClick={() => setSelectedTransition(null)}>Cancel</Button>
                  </Space>
                </Form.Item>
              </Form>
            </div>
          )}
        </div>
      )}
    </Modal>
  );
};

